import React, { useState } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MoreVertical, Download, Trash2, SquarePen } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { formatCompactNumber, formatPercentage } from '@/lib/utils/formatNumber';
import { STATUS_LABELS, type ContentStatus } from '../../services/contentStatus.service';
import { MetricsHistoryPopover } from '../modals/MetricsHistoryPopover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { EditPostModal } from '../modals/EditPostModal';
import { useDeleteContentSubmission } from '../../hooks/useContentSubmissions';
import { useScrapePostMetrics } from '../../hooks/useScrapePostMetrics';
import type { ContentSubmission } from '../../types/content.types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PostListRowProps {
  post: ContentSubmission;
  influencerFollowers: number | null;
}

const TYPE_LABELS: Record<string, string> = {
  reel: 'Reel',
  story: 'Story',
  post: 'Post',
  video: 'Video',
  carousel: 'Carousel',
  igtv: 'IGTV',
};

export const PostListRow: React.FC<PostListRowProps> = ({ post, influencerFollowers }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const deleteSubmission = useDeleteContentSubmission();
  const { scrape, isLoading: isScraping, cooldownMinutes } = useScrapePostMetrics(
    post.id,
    post.campaign_influencer_id
  );

  const metrics = post.performance_metrics;

  const erFollowers: number | null =
    post.er_followers !== null && post.er_followers !== undefined
      ? post.er_followers
      : influencerFollowers && influencerFollowers > 0 && metrics
        ? ((metrics.likes || 0) + (metrics.comments || 0)) / influencerFollowers * 100
        : null;

  const totalEngagementRaw = (metrics?.likes || 0) + (metrics?.comments || 0) + (metrics?.shares || 0);
  const erViews = metrics?.views && metrics.views > 0
    ? (totalEngagementRaw / metrics.views) * 100
    : null;
  const viewRate = influencerFollowers && influencerFollowers > 0 && metrics?.views
    ? (metrics.views / influencerFollowers) * 100
    : null;

  const handleDelete = () => {
    deleteSubmission.mutate({
      id: post.id,
      campaignInfluencerId: post.campaign_influencer_id,
    });
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <TableRow className="text-sm">
        {/* Platform (Clickable Thumbnail + Platform Logo Badge) */}
        <TableCell className="py-2 pl-8">
          {post.thumbnail_url ? (
            post.post_url ? (
              <a
                href={post.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-10 h-14 rounded-lg overflow-hidden border border-gray-200/90 bg-gray-900 shrink-0 block ring-1 ring-black/5 shadow-xs transition-all duration-200 hover:shadow-md hover:border-purple-400"
                title={`Open ${post.type || 'post'}`}
              >
                <img
                  src={post.thumbnail_url}
                  alt={post.type}
                  className="w-full h-full object-cover object-center transition-transform duration-200 group-hover:scale-105"
                />
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-white/95 shadow-xs flex items-center justify-center">
                  <img
                    src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                    alt="Instagram"
                    className="w-3 h-3 object-contain"
                  />
                </div>
              </a>
            ) : (
              <div className="relative w-10 h-14 rounded-lg overflow-hidden border border-gray-200/90 bg-gray-900 shrink-0 ring-1 ring-black/5 shadow-xs">
                <img
                  src={post.thumbnail_url}
                  alt={post.type}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-white/95 shadow-xs flex items-center justify-center">
                  <img
                    src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                    alt="Instagram"
                    className="w-3 h-3 object-contain"
                  />
                </div>
              </div>
            )
          ) : (
            post.post_url ? (
              <a
                href={post.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-10 h-14 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shrink-0 flex items-center justify-center hover:border-purple-300 transition-all shadow-xs"
                title="Open post"
              >
                <img
                  src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                  alt="Instagram"
                  className="w-5 h-5 object-contain"
                />
              </a>
            ) : (
              <div className="w-10 h-14 rounded-lg bg-gray-100 border border-gray-200 shrink-0 flex items-center justify-center">
                <img
                  src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                  alt="Instagram"
                  className="w-5 h-5 object-contain opacity-60"
                />
              </div>
            )
          )}
        </TableCell>

        {/* Post Type */}
        <TableCell className="py-2 text-center capitalize font-medium text-gray-700">
          {TYPE_LABELS[post.type] || post.type}
        </TableCell>

        {/* Views */}
        <TableCell className="py-2 text-center font-medium">
          {formatCompactNumber(metrics?.views)}
        </TableCell>

        {/* Likes */}
        <TableCell className="py-2 text-center font-medium">
          {formatCompactNumber(metrics?.likes)}
        </TableCell>

        {/* Comments */}
        <TableCell className="py-2 text-center font-medium">
          {formatCompactNumber(metrics?.comments)}
        </TableCell>

        {/* Shares */}
        <TableCell className="py-2 text-center font-medium">
          {formatCompactNumber(metrics?.shares)}
        </TableCell>

        {/* Post Eng. */}
        <TableCell className="py-2 text-center font-medium">
          {formatCompactNumber(totalEngagementRaw)}
        </TableCell>

        {/* ER% Followers */}
        <TableCell className="py-2 text-center font-medium">
          {erFollowers !== null ? formatPercentage(erFollowers) : '-'}
        </TableCell>

        {/* ER% Views */}
        <TableCell className="py-2 text-center font-medium">
          {erViews !== null ? formatPercentage(erViews) : '-'}
        </TableCell>

        {/* View Rate */}
        <TableCell className="py-2 text-center font-medium">
          {viewRate !== null ? formatPercentage(viewRate) : '-'}
        </TableCell>

        {/* Actions */}
        <TableCell className="py-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 bg-[#FAF5FF] hover:bg-[#F3E8FF] border border-[#A655F8]/10 text-[#A655F8] rounded-xl flex items-center justify-center transition-colors shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none cursor-pointer"
                >
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[320px] rounded-[24px] p-2 border border-slate-100 shadow-xl bg-white font-['Hanken_Grotesk',sans-serif] focus:outline-none">
                {post.post_url && post.post_url.includes('instagram.com') && (
                  <>
                    <DropdownMenuItem
                      onSelect={scrape}
                      disabled={isScraping || !!cooldownMinutes}
                      className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#FAF5FF] outline-none border-none"
                    >
                      <div className="w-12 h-12 rounded-[16px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#A655F8]/5">
                        {isScraping ? (
                          <LoadingSpinner size="small" iconClassName="w-5 h-5 text-[#A655F8]" />
                        ) : (
                          <Download className="w-5 h-5" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-[15px] font-semibold text-slate-800">
                          {isScraping ? 'Fetching…' : cooldownMinutes ? `Wait ${cooldownMinutes}m` : post.scrape_status === 'failed' ? 'Retry Fetch' : 'Fetch Metrics'}
                        </div>
                        <div className="text-[13px] text-slate-400 font-normal mt-0.5">Get latest campaign data</div>
                      </div>
                    </DropdownMenuItem>
                    <div className="h-px bg-slate-100 my-1.5" />
                  </>
                )}

                <DropdownMenuItem onSelect={() => setEditModalOpen(true)} className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#EFF6FF] outline-none border-none">
                  <div className="w-12 h-12 rounded-[16px] bg-[#EFF6FF] flex items-center justify-center text-[#3B82F6] shrink-0 border border-[#3B82F6]/5">
                    <SquarePen className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-[15px] font-semibold text-slate-800">Edit Post</div>
                    <div className="text-[13px] text-slate-400 font-normal mt-0.5">Update post details</div>
                  </div>
                </DropdownMenuItem>

                <div className="h-px bg-slate-100 my-1.5" />

                <DropdownMenuItem
                  onSelect={() => setDeleteDialogOpen(true)}
                  className="p-3 flex items-start gap-4 hover:bg-slate-50 transition-colors cursor-pointer rounded-2xl focus:bg-[#FEF2F2] outline-none border-none"
                >
                  <div className="w-12 h-12 rounded-[16px] bg-[#FEF2F2] flex items-center justify-center text-[#EF4444] shrink-0 border border-[#EF4444]/5">
                    <Trash2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-[15px] font-semibold text-[#EF4444]">Delete Post</div>
                    <div className="text-[13px] text-slate-400 font-normal mt-0.5">Remove post permanently</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <MetricsHistoryPopover
              contentSubmissionId={post.id}
              open={historyOpen}
              onOpenChange={setHistoryOpen}
              iconOnly
            />
          </div>
        </TableCell>
      </TableRow>

      <EditPostModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        post={post}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-[24px] border-slate-100 p-6 font-['Hanken_Grotesk',sans-serif] focus:outline-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-800">Delete Post</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 text-sm mt-2">
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex items-center justify-end gap-3 pt-4 border-none">
            <AlertDialogCancel className="rounded-xl h-11 px-6 hover:bg-slate-50 text-slate-700 border-slate-200 font-semibold text-sm transition-colors">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-xl h-11 px-6 font-semibold text-sm transition-colors border border-transparent"
            >
              Delete Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
